/**
 * 결정적 균형 검사기 — 결정 #30(자동 디버깅/수리)의 1단계. AI에 보내기 **전에**
 * 무료·즉시로 흔한 구문 오류(괄호·Lua `end`)를 감지하고, 고신뢰
 * 케이스만 보수적으로 자동 수리한다. 이렇게 1차로 걸러야 AI 수리 왕복(토큰=사용자
 * 비용)과 반복 횟수가 줄어든다.
 *
 * 설계 원칙(중요):
 *  - **감지는 항상 안전**: 문자열·주석 안의 괄호는 세지 않는다(오탐 방지). 위치(줄/열)를
 *    돌려줘 AI·사람이 정확히 어디인지 알 수 있게 한다 — 이 진단이 AI 수리 성공률을
 *    크게 높인다.
 *  - **자동 삽입은 보수적**: "여는 괄호가 더 많고 전부 끝까지 안 닫힘"처럼 EOF에 닫개를
 *    붙이는 건 *추정*이다. 그래서 자동수리 결과는 항상 diff로 보여주고(UI), 닫개만 있고
 *    여는 짝이 없는 경우(unmatched)는 절대 자동 삽입하지 않는다.
 *  - 플랫폼 중립(결정 #10): DOM/네트워크 의존 0. 순수 함수라 테스트로 사양을 고정한다.
 */

/** 검사 대상 언어/문법. */
export type RepairLang = "javascript" | "lua";

export interface BalanceIssue {
  /**
   * - `unclosed`: 여는 토큰이 끝까지 안 닫힘(예: `(` 만 있고 `)` 없음).
   * - `unmatched`: 닫는 토큰의 짝이 종류가 다름(예: `(` 를 `]` 로 닫음).
   * - `unopened`: 닫는 토큰만 있고 여는 짝이 없음.
   * - `keyword`: Lua `end` 개수 불일치(추정).
   * - `string`: 따옴표 문자열이 닫히지 않음(ASI와 무관한 진짜 구문 오류).
   */
  kind: "unclosed" | "unmatched" | "unopened" | "keyword" | "string";
  /** 관련 토큰(`(`, `}`, `end` 등). */
  token: string;
  /** 한국어 + AI가 읽기 좋은 설명. */
  message: string;
  /** 1-기반 줄 번호(keyword는 0일 수 있음 — 전체 집계라 위치 불명확). */
  line: number;
  /** 1-기반 열 번호(불명확하면 0). */
  column: number;
}

export interface AutoFix {
  /** 무엇을 어떻게 고쳤는지(diff 설명용). */
  description: string;
}

export interface RepairResult {
  /** 자동 수리가 적용된 결과 소스(아무것도 안 했으면 원본과 동일). */
  fixed: string;
  /** 적용된 수리 목록(UI가 diff로 보여줄 근거). */
  applied: AutoFix[];
  /** 자동 수리로 못 고친 채 남은 문제(사람/AI에게 넘김). */
  remaining: BalanceIssue[];
}

const PAIRS: Record<string, string> = { "(": ")", "[": "]", "{": "}" };
const CLOSERS = new Set([")", "]", "}"]);

interface OpenItem {
  char: string;
  line: number;
  column: number;
}

interface ScanResult {
  /** 닫히지 않은 여는 괄호들(스택에 남은 것). 위치순. */
  unclosed: OpenItem[];
  /** 짝이 안 맞거나 여는 짝이 없는 닫는 괄호들. */
  bad: BalanceIssue[];
}

/**
 * 문자열·주석을 건너뛰며 괄호 균형을 스캔한다(JS/Lua 공통, 언어별 주석/문자열 규칙만
 * 다름). 핵심: 코드 텍스트의 괄호만 세서 `"(emoji)"` 같은 리터럴 오탐을 막는다.
 */
function scanBrackets(source: string, lang: RepairLang): ScanResult {
  const stack: OpenItem[] = [];
  const bad: BalanceIssue[] = [];
  let line = 1;
  let col = 1;
  const n = source.length;
  let i = 0;

  const peek = (o = 0) => source[i + o] ?? "";
  const advance = () => {
    const ch = source[i]!;
    if (ch === "\n") {
      line++;
      col = 1;
    } else {
      col++;
    }
    i++;
    return ch;
  };

  while (i < n) {
    const ch = peek();

    // --- 문자열 ---
    if (ch === '"' || ch === "'" || (lang === "javascript" && ch === "`")) {
      const quote = ch;
      const startLine = line;
      const startCol = col;
      const multiline = quote === "`"; // JS 템플릿 리터럴만 여러 줄 허용
      advance();
      let closed = false;
      while (i < n) {
        const c = peek();
        if (c === "\\") {
          advance();
          advance();
          continue;
        }
        if (c === quote) {
          advance();
          closed = true;
          break;
        }
        // 여러 줄 불가 문자열('/")이 줄바꿈을 만나면 닫히지 않은 것으로 보고 스캔 재개.
        if (c === "\n" && !multiline) break;
        advance();
      }
      if (!closed) {
        const isTemplate = multiline;
        bad.push({
          kind: "string",
          token: quote,
          message:
            startLine +
            "번째 줄 " +
            startCol +
            "열에서 시작된 " +
            (isTemplate ? "템플릿 " : "") +
            "문자열이 닫히지 않았습니다(따옴표 짝을 확인하세요).",
          line: startLine,
          column: startCol,
        });
      }
      continue;
    }

    // --- Lua 긴 문자열/긴 주석 [[ ... ]] (레벨 [=[ 는 단순화로 [[만 처리) ---
    if (lang === "lua" && ch === "[" && (peek(1) === "[" || peek(1) === "=")) {
      const start = i;
      // [[ 또는 [=*[ 매칭.
      let j = i + 1;
      let eq = 0;
      while (source[j] === "=") {
        eq++;
        j++;
      }
      if (source[j] === "[") {
        // 긴 괄호 시작. 닫기 ]=*] 까지 스킵.
        const close = "]" + "=".repeat(eq) + "]";
        const end = source.indexOf(close, j + 1);
        const stop = end === -1 ? n : end + close.length;
        while (i < stop) advance();
        continue;
      }
      // [[가 아니면 일반 [ 로 처리(아래로 진행).
      void start;
    }

    // --- 주석 ---
    if (lang === "javascript" && ch === "/" && peek(1) === "/") {
      while (i < n && peek() !== "\n") advance();
      continue;
    }
    if (lang === "javascript" && ch === "/" && peek(1) === "*") {
      advance();
      advance();
      while (i < n && !(peek() === "*" && peek(1) === "/")) advance();
      advance();
      advance();
      continue;
    }
    if (lang === "lua" && ch === "-" && peek(1) === "-") {
      // 긴 주석 --[[ ]] 은 위 긴 괄호 처리가 못 잡으므로 여기서 라인 주석만.
      while (i < n && peek() !== "\n") advance();
      continue;
    }

    // --- 괄호 ---
    if (ch === "(" || ch === "[" || ch === "{") {
      stack.push({ char: ch, line, column: col });
      advance();
      continue;
    }
    if (CLOSERS.has(ch)) {
      const top = stack[stack.length - 1];
      if (!top) {
        bad.push({
          kind: "unopened",
          token: ch,
          message: `${line}번째 줄 ${col}열의 '${ch}'에 짝이 되는 여는 괄호가 없습니다.`,
          line,
          column: col,
        });
      } else if (PAIRS[top.char] !== ch) {
        bad.push({
          kind: "unmatched",
          token: ch,
          message: `${line}번째 줄의 '${ch}'가 ${top.line}번째 줄에서 열린 '${top.char}'와 짝이 맞지 않습니다('${PAIRS[top.char]}'가 와야 합니다).`,
          line,
          column: col,
        });
        stack.pop(); // 관대하게 팝해 이후 검사를 계속.
      } else {
        stack.pop();
      }
      advance();
      continue;
    }

    advance();
  }

  return { unclosed: stack, bad };
}

/** Lua `end` 균형(추정). 문자열·주석을 건너뛴 식별자 토큰만 세서 키워드 개수를 비교. */
function scanLuaEnd(source: string): BalanceIssue | null {
  // 문자열/주석을 공백으로 치환한 뒤 단어 토큰만 본다(scanBrackets와 같은 정신).
  const cleaned = stripStringsAndComments(source, "lua");
  const words = cleaned.match(/[A-Za-z_]\w*/g) ?? [];
  let openers = 0;
  let pendingForWhile = false; // for/while 의 do 는 그 블록의 일부라 따로 안 센다.
  let ends = 0;
  for (const w of words) {
    switch (w) {
      case "function":
      case "if":
        openers++;
        break;
      case "for":
      case "while":
        openers++;
        pendingForWhile = true;
        break;
      case "do":
        if (pendingForWhile) {
          pendingForWhile = false; // for/while 의 do — 이미 센 블록.
        } else {
          openers++; // 독립 do 블록.
        }
        break;
      case "end":
        ends++;
        break;
      default:
        break;
    }
  }
  if (openers === ends) return null;
  const diff = openers - ends;
  return diff > 0
    ? {
        kind: "keyword",
        token: "end",
        message: `Lua 블록 여는 키워드(function/if/for/while/do)가 'end'보다 ${diff}개 많습니다. 'end'가 ${diff}개 빠졌을 수 있습니다(추정).`,
        line: 0,
        column: 0,
      }
    : {
        kind: "keyword",
        token: "end",
        message: `'end'가 블록 여는 키워드보다 ${-diff}개 많습니다. 불필요한 'end'가 있을 수 있습니다(추정).`,
        line: 0,
        column: 0,
      };
}

/** 문자열/주석을 같은 길이의 공백으로 치환(토큰 카운팅용, 위치 보존). */
function stripStringsAndComments(source: string, lang: RepairLang): string {
  const out = source.split("");
  let i = 0;
  const n = source.length;
  const blank = (from: number, to: number) => {
    for (let k = from; k < to && k < n; k++) {
      if (out[k] !== "\n") out[k] = " ";
    }
  };
  while (i < n) {
    const ch = source[i]!;
    const next = source[i + 1] ?? "";
    if (ch === '"' || ch === "'" || (lang === "javascript" && ch === "`")) {
      const start = i;
      i++;
      while (i < n) {
        if (source[i] === "\\") {
          i += 2;
          continue;
        }
        if (source[i] === ch) {
          i++;
          break;
        }
        i++;
      }
      blank(start, i);
      continue;
    }
    if (lang === "lua" && ch === "-" && next === "-") {
      const start = i;
      while (i < n && source[i] !== "\n") i++;
      blank(start, i);
      continue;
    }
    if (lang === "javascript" && ch === "/" && next === "/") {
      const start = i;
      while (i < n && source[i] !== "\n") i++;
      blank(start, i);
      continue;
    }
    if (lang === "javascript" && ch === "/" && next === "*") {
      const start = i;
      i += 2;
      while (i < n && !(source[i] === "*" && source[i + 1] === "/")) i++;
      i += 2;
      blank(start, i);
      continue;
    }
    i++;
  }
  return out.join("");
}

/**
 * 소스의 균형 문제를 감지한다(자동 수리 없음 — 항상 안전). 빈 배열이면 균형이 맞다.
 */
export function analyzeBalance(source: string, lang: RepairLang): BalanceIssue[] {
  const issues: BalanceIssue[] = [];

  const { unclosed, bad } = scanBrackets(source, lang);
  issues.push(...bad);
  for (const open of unclosed) {
    issues.push({
      kind: "unclosed",
      token: open.char,
      message: `${open.line}번째 줄 ${open.column}열에서 열린 '${open.char}'가 닫히지 않았습니다('${PAIRS[open.char]}' 필요).`,
      line: open.line,
      column: open.column,
    });
  }

  if (lang === "lua") {
    const end = scanLuaEnd(source);
    if (end) issues.push(end);
  }

  return issues;
}

/**
 * 보수적 자동 수리(결정 #30). **고신뢰 케이스만** 고친다:
 *  - 닫히지 않은 여는 괄호(unclosed) → 역순으로 EOF에 닫개 추가.
 * `unmatched`/`unopened`(닫개가 잘못/여분)는 위치를 추정하면 의미가 틀어질 위험이
 * 커서 **건드리지 않고 remaining 으로 넘긴다**. 결과는 항상 diff로 보여주는 것을 전제.
 *
 * 주의: EOF 추가는 "추정"이다(중간에 들어가야 할 수도 있음). 그래서 호출부는 자동
 * 적용 대신 제안(diff 확인)을 기본으로 둬야 한다.
 */
export function autoRepair(source: string, lang: RepairLang): RepairResult {
  const { unclosed, bad } = scanBrackets(source, lang);
  const applied: AutoFix[] = [];
  let suffix = "";

  // 닫히지 않은 괄호를 역순(가장 안쪽부터)으로 닫는다.
  for (let k = unclosed.length - 1; k >= 0; k--) {
    const open = unclosed[k]!;
    suffix += PAIRS[open.char];
    applied.push({
      description: `${open.line}번째 줄에서 열린 '${open.char}'를 닫는 '${PAIRS[open.char]}'를 끝에 추가했습니다.`,
    });
  }

  const fixed = source + suffix;
  // bad(unmatched/unopened/string)와 Lua end 불일치는 자동 수리 대상이 아니므로 그대로
  // 남긴다(닫히지 않은 문자열은 위치 추정이 위험해 사용자/AI에게 넘긴다).
  const remaining = analyzeBalance(fixed, lang).filter(
    (x) =>
      x.kind === "unmatched" ||
      x.kind === "unopened" ||
      x.kind === "keyword" ||
      x.kind === "string",
  );
  // 자동 수리로 못 건드린 bad도 remaining에 포함(분석이 이미 재검출).
  void bad;
  return { fixed, applied, remaining };
}
